import React from 'react';
import { motion } from 'framer-motion';
import { BoxIcon } from 'lucide-react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: BoxIcon;
  href?: string;
  withArrow?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  href,
  withArrow,
  fullWidth,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const baseStyles = 'relative inline-flex items-center justify-center font-medium transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0A0F2A] disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden';
  const variants = {
    primary: 'text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 border-0 bg-blue-600',
    secondary: 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/10',
    outline: 'bg-transparent text-white border border-white/20 hover:border-white/50 hover:bg-white/5'
  };
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  const widthClass = fullWidth ? 'w-full' : '';
  // Check if className contains custom gradient override
  const hasCustomGradient = className.includes('!bg-gradient-to-r');
  const content = <>
      {variant === 'primary' && !hasCustomGradient && <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 animate-gradient-xy" data-id="element-637" />}

      {variant === 'primary' && hasCustomGradient && <div className={`absolute inset-0 ${className.match(/!bg-gradient-to-r[^\s]*/)?.[0]?.replace(/!/g, '') || ''} ${className.match(/!from-[^\s]*/)?.[0]?.replace(/!/g, '') || ''} ${className.match(/!via-[^\s]*/)?.[0]?.replace(/!/g, '') || ''} ${className.match(/!to-[^\s]*/)?.[0]?.replace(/!/g, '') || ''} animate-gradient-xy`} data-id="element-638" />}

      {variant === 'primary' && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" data-id="element-639" />}

      <span className="relative flex items-center gap-2 z-10" data-id="element-640">
        {Icon && <Icon className="w-4 h-4" data-id="element-641" />}
        {children}
        {withArrow && <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-642">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-643" />
          </svg>}
      </span>
    </>;
  // Clean className from gradient overrides for the outer element
  const cleanClassName = className.replace(/!bg-gradient-to-r[^\s]*/g, '').replace(/!from-[^\s]*/g, '').replace(/!via-[^\s]*/g, '').replace(/!to-[^\s]*/g, '').trim();
  if (href) {
    return <motion.a href={href} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${cleanClassName} group`} data-id="element-644">
        {content}
      </motion.a>;
  }
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${cleanClassName} group`} {...props} data-id="element-645">
      {content}
    </motion.button>;
}